import { useState } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const BetaAccessModal = ({ isOpen, onClose, onSignIn, onRegister }) => {
  if (!isOpen) return null
  const [showInterestForm, setShowInterestForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: '',
    location: '',
    reason: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleInterestSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await apiService.betaSignup(formData)

      console.log('Beta signup email sent:', formData)

      // Fire Google Ads conversion event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17812923441/8YR4CP6fgtQbELHI7q1C'
        })
      }

      setIsSubmitting(false)
      setSubmitSuccess(true)

      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Failed to send beta signup email:', error)
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800/90 rounded-2xl border border-white/10 p-8 max-w-md w-full relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
          
          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-2">Thank you!</h3>
            <p className="text-gray-300">We'll be in touch soon with updates about the beta.</p>
          </div>
        </div>
      </div>
    )
  }

  if (showInterestForm) {
    return (
      <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800/90 rounded-2xl border border-white/10 p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
          
          <button
            onClick={() => setShowInterestForm(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-2">Join the Beta</h2>
            <p className="text-gray-300 mb-6">Help us shape the future of idea validation</p>

            <form onSubmit={handleInterestSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
                  Full Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-sm text-white disabled:opacity-50"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-sm text-white disabled:opacity-50"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="userType" className="block text-sm font-medium text-gray-200 mb-2">
                  I'm interested as a(n) *
                </label>
                <select
                  id="userType"
                  name="userType"
                  required
                  value={formData.userType}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-sm text-white disabled:opacity-50"
                >
                  <option value="">Select one</option>
                  <option value="individual">Individual</option>
                  <option value="entrepreneur">Business Entrepreneur</option>
                  <option value="sme">SME (Small/Medium Enterprise)</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-200 mb-2">
                  Geographic Location *
                </label>
                <input
                  id="location"
                  name="location"
                  type="text"
                  required
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-sm text-white disabled:opacity-50"
                  placeholder="e.g., San Francisco, CA or London, UK"
                />
              </div>

              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-200 mb-2">
                  Why are you interested in Opptora? (Optional)
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows="4"
                  value={formData.reason}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-sm text-white disabled:opacity-50 resize-none"
                  placeholder="Tell us about your use case, goals, or what problem you're trying to solve..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Interest'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800/90 rounded-2xl border border-white/10 p-8 max-w-lg w-full relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
        
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="relative z-10">
          <div className="flex items-center justify-center mb-6">
            <img src={`${import.meta.env.BASE_URL}images/logo.png`} alt="Opptora Logo" className="size-16" />
          </div>

          <h2 className="text-3xl font-bold text-white text-center mb-3">Welcome to Opptora</h2>
          <p className="text-gray-300 text-center mb-6">
            We're currently in <span className="text-sky-300 font-semibold">closed beta</span>
          </p>

          <div className="bg-blue-900/30 border border-blue-800 rounded-xl p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-200">
                  Join our beta to get early access and help shape the future of idea validation
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setShowInterestForm(true)}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Register Interest for Beta
            </button>

            <button
              onClick={onRegister}
              className="w-full flex justify-center py-3 px-4 border border-gray-600 text-sm font-medium rounded-xl text-white bg-gray-900/50 hover:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
            >
              I Have an Invite Code
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800/90 text-gray-400">or</span>
              </div>
            </div>

            <button
              onClick={onSignIn}
              className="w-full flex justify-center py-3 px-4 border border-gray-600 text-sm font-medium rounded-xl text-gray-300 bg-transparent hover:bg-gray-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
            >
              Sign In to Existing Account
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

BetaAccessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
}

export default BetaAccessModal
